import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { Folder } from './folderSlice';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';

export interface ImageGenerationStatus {
  success: boolean;
  assets_generating: boolean;
  total_pages: number;
  pages_with_images: number;
  should_generate_asset: boolean;
  is_complete: boolean;
}

export interface ProjectSettings {
  title: string;
  font: string;
  text_size: 'Small' | 'Medium' | 'Large';
  branding_hidden: boolean;
  close_conversation: boolean;
  tone_of_voice?: string;
  logo: File | null;
  logo_url: string | null;
  welcome_title?: string;
  welcome_message?: string;
  welcome_call_to_action?: string;
  end_title?: string;
  end_message?: string;
  end_call_to_action?: string;
  custom_css?: string;
  background_color?: string;
  text_color?: string;
  project_language?: string | null;
  button_color?: string;
  notification_email?: string;
  form_password?: string;
  form_password_enabled?: boolean;
  analytics_site_id?: string;
  show_count_bar?: boolean;
  skip_welcome_screen?: boolean;
  enable_restore_chat?: boolean;
  enable_submission_limit?: boolean;
  submission_limit?: number;
  close_after_submission_limit?: boolean;
  enable_respondent_notifications?: boolean;
  enable_thank_you_email?: boolean;
  thank_you_email_template?: string;
  thank_you_email_format?: 'html_css_enabled_style' | 'plain_text';
  show_question_clarity?: boolean;
  show_other_respondents?: boolean;
  track_user_source?: boolean;
  show_user_details_filled_end_of_screen?: boolean;
  allow_edit_details_end_of_screen?: boolean;
  should_assist_user_with_details?: boolean;
  should_enable_audio_reply?: boolean;
  disable_typing_animation?: boolean;
}

export interface Project {
  conversation_entries?: any[];
  conversation_id?: number;
  form_page?: any;
  id: number;
  name: string;
  project_overview?: string;
  slug?: string;
  comments?: any[];
  settings?: ProjectSettings;
  shareable_link?: string;
  folder_id?: number;
  organization_id?: number;
  visibility: 'public' | 'private';
  project_type: 'guided_forms' | 'adaptive_forms';
  cover_image?: string | null;
  conversations_count?: number;
}

interface CreateProjectData {
  name: string;
  project_type: 'guided_forms' | 'adaptive_forms';
  organization_id?: number;
  folder_id?: number;
  visibility?: 'private' | 'public';
  shareable_link?: string;
}

export interface Project {
  id: number;
  project_title?: string;
  folder_id?: number;
  name: string;
  project_overview?: string;
  slug?: string;
  comments?: any[];
  settings?: ProjectSettings;
  organization_id?: number;
  project_type: 'guided_forms' | 'adaptive_forms';
  // Add other project properties as needed
}

export interface Template {
  id: number;
  title: string;
  description: string;
  tag?: string;
  icon: string;
}

export interface TrainingDataItem {
  id: number;
  data_type: 'file' | 'url' | 'text';
  is_processed: boolean;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  file_url?: string;
  content?: string;
}

interface ProjectState {
  currentProject: Project | null;
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  currentFolder: Folder | null;
  projectSettings: ProjectSettings | null;
  templates: Template[];
  isLoadingTemplates: boolean;
  trainingData: TrainingDataItem[];
  imageGenerationStatus: ImageGenerationStatus | null;
  isCheckingImageGeneration: boolean;
}
   
const CONVERSATION_ID_COOKIE_PRIVATE = 'form_conversation_id_private';

export const saveConversationIdToCookiePrivate = (conversationId: string, projectId: string | number | undefined) => {
  Cookies.set(CONVERSATION_ID_COOKIE_PRIVATE+"_"+projectId, conversationId, { expires: 1 });
};

export const getConversationIdFromCookiePrivate = (projectId: string | number | undefined): string | undefined => {
  return Cookies.get(CONVERSATION_ID_COOKIE_PRIVATE+"_"+projectId);
};

export const removeConversationIdCookiePrivate = (projectId: string | number | undefined) => {
  Cookies.remove(CONVERSATION_ID_COOKIE_PRIVATE+"_"+projectId);
};

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: CreateProjectData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/projects', projectData);
      return response.data.project;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchFolderProjects = createAsyncThunk(
  'projects/fetchFolderProjects',
  async (folderId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/folders/${folderId}/projects`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (
    { projectIdOrSlug, conversationId }: { projectIdOrSlug: number | string; conversationId?: string },
    { rejectWithValue }
  ) => {
    try {
      let url = `/projects/${projectIdOrSlug}`;
      if (conversationId) {
        url += `?conversation_id=${conversationId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data.project;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (
    { id, data }: { id: number; data: Partial<Project> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(`/projects/${id}`, data);
      return response.data.project;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateProjectSettings = createAsyncThunk(
  'projects/updateSettings',
  async (
    {
      projectIdOrSlug,
      settings,
    }: {
      projectIdOrSlug: string | number | undefined;
      settings: Partial<ProjectSettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      Object.entries(settings).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/settings`,
        formData
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchProjectSettings = createAsyncThunk(
  'projects/fetchProjectSettings',
  async (projectIdOrSlug: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/settings`
      );
      return response.data.settings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const uploadAITrainingFile = createAsyncThunk(
  'projects/uploadAITrainingFile',
  async (
    { projectId, file }: { projectId: number; file: File },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('project_id', projectId.toString());
      formData.append('data_type', 'file');
      formData.append('file', file);

      const response = await axiosInstance.post(
        `/projects/${projectId}/ai-training-data`,
        formData,
        {}
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const saveAITrainingText = createAsyncThunk(
  'projects/saveAITrainingText',
  async (
    { projectId, text }: { projectId: number; text: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectId}/ai-training-data`,
        {
          project_id: projectId,
          data_type: 'text',
          content: text,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchAITrainingUrl = createAsyncThunk(
  'projects/fetchAITrainingUrl',
  async (
    { projectId, url }: { projectId: number; url: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectId}/ai-training-data`,
        {
          project_id: projectId,
          data_type: 'url',
          content: url,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const createProjectFromScratch = createAsyncThunk(
  'projects/createProjectFromScratch',
  async (
    {
      organizationId,
      folderId,
      projectData,
    }: {
      organizationId: number;
      folderId: number;
      projectData: {
        project_title: string;
        overview: string;
        project_type: string;
        project_language: string;
        tone_of_voice: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${organizationId}/folders/${folderId}/new-projects/generate-content`,
        projectData
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        return rejectWithValue(
          error.response.data.errors || error.response.data.message
        );
      } else if (error.response?.status === 400) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const generateProjectContent = createAsyncThunk(
  'projects/generateProjectContent',
  async (
    {
      organizationId,
      folderId,
      projectId,
      data,
    }: {
      organizationId: number | undefined;
      folderId: number | undefined;
      projectId: number | undefined;
      data: {
        project_title?: string;
        overview: string;
        include_assets: boolean;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${organizationId}/folders/${folderId}/projects/${projectId}/generate-content`,
        data
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        return rejectWithValue(
          error.response.data.errors || error.response.data.message
        );
      } else if (error.response?.status === 400) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const moveProject = createAsyncThunk(
  'projects/moveProject',
  async (
    { projectId, folderId }: { projectId: number; folderId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(`/projects/${projectId}/move`, {
        folder_id: folderId,
      });
      return response.data;
    } catch (err) {
      const error = err as AxiosError;
      return rejectWithValue(error.response?.data || 'Failed to move project');
    }
  }
);

export const duplicateProject = createAsyncThunk(
  'projects/duplicateProject',
  async (
    {
      projectIdOrSlug,
      folderId,
      newName,
    }: {
      projectIdOrSlug: string | number;
      folderId: number;
      newName?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        `/projects/${projectIdOrSlug}/duplicate`,
        {
          folder_id: folderId,
          new_name: newName,
        }
      );
      return response.data.project;
    } catch (err: any) {
      const error = err as AxiosError;
      return rejectWithValue(error.response?.data || 'Failed to move project');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId: number, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/projects/${projectId}`);
      return projectId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchTemplates = createAsyncThunk(
  'project/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/templates/default');
      return response.data.templates;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const copyTemplate = createAsyncThunk(
  'project/copyTemplate',
  async (
    {
      templateId,
      folderId,
    }: {
      templateId: number;
      folderId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/templates/default/${templateId}/copy/${folderId}`,
        {}
      );
      return response.data.project;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchTrainingData = createAsyncThunk(
  'projects/fetchTrainingData',
  async (projectId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/training-data`);
      return response.data.training_data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const deleteTrainingData = createAsyncThunk(
  'projects/deleteTrainingData',
  async (
    { projectId, trainingDataId }: { projectId: number; trainingDataId: number },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/projects/${projectId}/ai-training-data/${trainingDataId}`
      );
      return trainingDataId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const checkImageGenerationStatus = createAsyncThunk(
  'projects/checkImageGenerationStatus',
  async (projectIdOrSlug: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/image-generation-status`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

const projectSlice = createSlice({
  name: 'project',
  initialState: {
    currentProject: null as Project | null,
    projects: [] as Project[],
    isLoading: false,
    error: null as string | null,
    currentFolder: null as Folder | null,
    projectSettings: null as ProjectSettings | null,
    templates: [],
    isLoadingTemplates: false,
    trainingData: [] as TrainingDataItem[],
    imageGenerationStatus: null as ImageGenerationStatus | null,
    isCheckingImageGeneration: false,
  },
  reducers: {
    resetProjectState: (state) => {
      state.currentProject = null;
      state.projectSettings = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProject.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProject = action.payload;
      })
      .addCase(
        createProject.rejected,
        (state: any, action: ReturnType<typeof createProject.rejected>) => {
          state.isLoading = false;
          state.error = action.payload as string;
        }
      )
      .addCase(fetchFolderProjects.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFolderProjects.fulfilled, (state, action) => {
        state.projects = action.payload.projects;
        state.currentFolder = action.payload.folder;
      })
      .addCase(fetchFolderProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProject.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProject = action.payload;
        if(action.payload?.conversation_entries && state.currentProject){
          state.currentProject.conversation_entries = action.payload.conversation_entries;
        }
        if(action.payload?.conversation_id && state.currentProject){
          state.currentProject.conversation_id = action.payload.conversation_id;
        }
        if(action.payload?.form_page && state.currentProject){
          state.currentProject.form_page = action.payload.form_page;
        }
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProject.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProject = action.payload;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProjectSettings.fulfilled, (state, action) => {
        state.projectSettings = action.payload;
        if (state.currentProject) {
          state.currentProject.settings = action.payload;
        }
      })
      .addCase(fetchProjectSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProjectSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projectSettings = action.payload;
      })
      .addCase(fetchProjectSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadAITrainingFile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(uploadAITrainingFile.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.trainingData = [...state.trainingData, action.payload.data];
        }
      })
      .addCase(uploadAITrainingFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(saveAITrainingText.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveAITrainingText.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.trainingData = [...state.trainingData, action.payload.data];
        }
      })
      .addCase(saveAITrainingText.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAITrainingUrl.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAITrainingUrl.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.trainingData = [...state.trainingData, action.payload.data];
        }
      })
      .addCase(fetchAITrainingUrl.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateProjectContent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateProjectContent.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.currentProject = action.payload.project;
          state.projectSettings = action.payload.project.settings;
        }
      })
      .addCase(generateProjectContent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createProjectFromScratch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProjectFromScratch.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.currentProject = action.payload.project;
        }
      })
      .addCase(createProjectFromScratch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(moveProject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(moveProject.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the project in the state with new folder_id
      })
      .addCase(moveProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(duplicateProject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(duplicateProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projects.push(action.payload);
      })
      .addCase(duplicateProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteProject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projects = state.projects.filter(
          (project) => project.id !== action.payload
        );
        if (state.currentProject?.id === action.payload) {
          state.currentProject = null;
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTemplates.pending, (state) => {
        state.isLoadingTemplates = true;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.isLoadingTemplates = false;
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state) => {
        state.isLoadingTemplates = false;
      })
      .addCase(copyTemplate.fulfilled, (state, action) => {
        // Optionally handle the newly created project
        state.currentProject = action.payload;
      })
      .addCase(fetchTrainingData.fulfilled, (state, action) => {
        state.trainingData = action.payload;
      })
      .addCase(deleteTrainingData.fulfilled, (state, action) => {
        state.trainingData = state.trainingData.filter(
          item => item.id !== action.payload
        );
      })
      .addCase(checkImageGenerationStatus.pending, (state) => {
        state.isCheckingImageGeneration = true;
      })
      .addCase(checkImageGenerationStatus.fulfilled, (state, action) => {
        state.isCheckingImageGeneration = false;
        state.imageGenerationStatus = action.payload;
      })
      .addCase(checkImageGenerationStatus.rejected, (state, action) => {
        state.isCheckingImageGeneration = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetProjectState } = projectSlice.actions;

export default projectSlice.reducer;
